import "./style.css";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import * as dat from "lil-gui";

/**
 * Base
 */
// Debug
const gui = new dat.GUI();

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Galaxy
 */
const parameters = {
  count: 100000,
  size: 0.015,
  radius: 5,
  branches: 4,
  spin: 1.2,
  randomness: 0.37,
  randomnessPower: 3,
  insideColor: "#ff6030",
  outsideColor: "#1b3984",
  branchMinHeight: 0.5,
};

let galaxyGeometry = null;
let galaxyMaterial = null;
let galaxy = null;

function getRandomPosition() {
  return (
    Math.pow(Math.random(), parameters.randomnessPower) *
    parameters.randomness *
    (Math.random() < 0.5 ? 1 : -1)
  );
}

// Decides the position of the point only in relation to a line
function calcPosOnRadius(radiusPos) {
  // Try to invert power, to place points more to the center of the galaxy
  return (radiusPos / parameters.radius) ** 2 * parameters.radius;
}

function generateVertexes(positions, colors) {
  positions = positions || galaxy.geometry.attributes.position.array;
  colors = colors || galaxy.geometry.attributes.color.array;

  const colorInside = new THREE.Color(parameters.insideColor);
  const colorOutside = new THREE.Color(parameters.outsideColor);

  for (let i = 0; i < parameters.count; i++) {
    const i3 = i * 3;

    // Position
    const radius = Math.random() * parameters.radius;
    const spinAngle = radius * parameters.spin;
    // i % 3 will make "i" alternate (0, 1, 2)
    // (i % 3) / 3 will make (0, 1, 2) into (0, 0.33, 0.66) resulting in percentage
    // having the percentage we just multiply by PI * 2, that represents a full circle
    const branchAngle =
      ((i % parameters.branches) / parameters.branches) * Math.PI * 2;

    const randomX = getRandomPosition();
    const randomY = getRandomPosition();
    const randomZ = getRandomPosition();

    positions[i3] =
      Math.cos(branchAngle + spinAngle) * calcPosOnRadius(radius) + randomX; // x
    positions[i3 + 1] =
      Math.max((parameters.radius - radius) / 4, parameters.branchMinHeight) *
      randomY; // y
    positions[i3 + 2] =
      Math.sin(branchAngle + spinAngle) * calcPosOnRadius(radius) + randomZ; // z

    // Color
    const mixedColor = colorInside.clone();
    mixedColor.lerp(colorOutside, radius / parameters.radius);

    colors[i3] = mixedColor.r;
    colors[i3 + 1] = mixedColor.g;
    colors[i3 + 2] = mixedColor.b;
  }

  // Will update only if necessary
  if (galaxyGeometry?.attributes?.position) {
    galaxyGeometry.attributes.position.needsUpdate = true;
    galaxyGeometry.attributes.color.needsUpdate = true;
  }
}

const generateGalaxy = () => {
  // Dispose of galaxy if it already exists
  if (galaxy !== null) {
    galaxyGeometry.dispose();
    galaxyMaterial.dispose();
    scene.remove(galaxy);
  }

  /**
   * Galaxy geometry
   */
  galaxyGeometry = new THREE.BufferGeometry();
  const positions = new Float32Array(parameters.count * 3);
  const colors = new Float32Array(parameters.count * 3);
  generateVertexes(positions, colors);

  galaxyGeometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3)
  );
  galaxyGeometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  /**
   * Galaxy material
   */
  galaxyMaterial = new THREE.PointsMaterial({
    color: "#fff",
    size: parameters.size,
    sizeAttenuation: true,
    depthWrite: true,
    blending: THREE.AdditiveBlending,
    vertexColors: true,
  });

  galaxy = new THREE.Points(galaxyGeometry, galaxyMaterial);
  scene.add(galaxy);
};

generateGalaxy();

gui
  .add(parameters, "count")
  .min(100)
  .max(1000000)
  .step(100)
  .onFinishChange(generateGalaxy);
gui
  .add(parameters, "size")
  .min(0.001)
  .max(0.1)
  .step(0.001)
  .onFinishChange(generateGalaxy);
gui
  .add(parameters, "radius")
  .min(0.01)
  .max(20)
  .step(0.01)
  .onFinishChange(generateGalaxy);
gui
  .add(parameters, "branches")
  .min(2)
  .max(20)
  .step(1)
  .onFinishChange(generateGalaxy);
gui
  .add(parameters, "spin")
  .min(-5)
  .max(5)
  .step(0.001)
  .onChange(() => generateVertexes());
gui
  .add(parameters, "randomness")
  .min(0)
  .max(2)
  .step(0.001)
  .onChange(() => generateVertexes());
gui
  .add(parameters, "randomnessPower")
  .min(1)
  .max(10)
  .step(0.001)
  .onChange(() => generateVertexes());
gui
  .add(parameters, "branchMinHeight")
  .min(0.01)
  .max(5)
  .step(0.01)
  .onChange(generateGalaxy);
gui.addColor(parameters, "insideColor").onFinishChange(generateGalaxy);
gui.addColor(parameters, "outsideColor").onFinishChange(generateGalaxy);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 3;
camera.position.y = 3;
camera.position.z = 3;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();

  // Animate galaxy
  galaxy.rotation.y = elapsedTime * 0.02;
  galaxy.rotation.x = Math.sin(elapsedTime * 0.5) / 45;

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
